/**
 * MembershipCard.js — Wine Club Membership Card (cardType "membership").
 *
 * THE common membership card, shared by every "rich winery card" tenant
 * (Goosecross, Halleck, Hestan, Valle — they built byte-identical DOM, only the
 * colours + fallback logo differed). Colours come from the shared cards.css
 * (themed via --ws-rich-* / admin theme); a tenant overrides a rule in its own
 * styles.css ONLY where it structurally differs. The fallback winery logo is a
 * config value (theme.card_logo_url) — per-winery data, not code.
 *
 * A tenant only needs its OWN MembershipCard component when its DOM structure
 * genuinely differs (e.g. Fogarty: no bullets, no price block, string desc).
 *
 * DOM: card > head>img(clickable) ; title ; inner > [price_additional_text,
 *      description bullets, Learn more, footer{price+unit, CTA}].
 *
 * BE FIELDS: title, description (array of bullet lines), price_additional_text,
 *   price, price_unit, cover_image_link, display_button, cta_text, cta_link,
 *   unique_id, message.
 *
 * LEARN MORE / image click → ws-prompt-select with unique_id (BE returns the
 * membership's detail card via handle_unique_id_lookup).
 */

import { emit, isChatDisabled, getState } from "../../core/state.js";
import { getTheme, isMembershipEnabled } from "../../core/configAccessors.js";
import { ASSETS_BASE_URL } from "../../core/env.js";
import { getDefaultCardLogo } from "../../core/defaultCardLogos.js";

const BOOK_ICON =
  '<svg viewBox="0 0 16 16" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M2.67 13V3a1.67 1.67 0 0 1 1.66-1.67H13v13.34H4.33A1.67 1.67 0 0 1 2.67 13zm0 0a1.67 1.67 0 0 1 1.66-1.67H13"/></svg>';

export const CHECK_ICON =
  '<svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true"><path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm-1.2 14.6L6 11.8l1.4-1.4 3.4 3.4 6.2-6.2 1.4 1.4-7.6 7.6z"/></svg>';

export const esc = (s) => String(s).replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));

function isTrue(v) {
  return v === true || v === "true" || v === "True";
}

export default class MembershipCard {
  render(club) {
    const card = document.createElement("div");
    card.className = "ws-card ws-membership-card ws-rich-membership";

    const detailLabel = club.message || `Tell me more about ${club.title || "this membership"}`;
    const openDetail = () =>
    {
      if(isChatDisabled()) return;
      emit("ws-prompt-select", { label: detailLabel, value: detailLabel, uniqueId: club.unique_id });
    }
    // Cover image (clickable → detail). Old Goose createWineMembership:
    // cover_image_link || defaultCardLogo (goose-bkg.webp). Here the default
    // is theme.card_logo_url — absolute URLs as-is, relative under ASSETS_BASE_URL.
    const head = document.createElement("div");
    head.className = "ws-card-head";
    const cover = String(club.cover_image_link || "").trim();
    const logoPath = getTheme("card_logo_url");
    let logoSrc = null;
    if (logoPath) {
      logoSrc = /^https?:\/\//i.test(logoPath)
        ? logoPath
        : `${ASSETS_BASE_URL}${logoPath}`;
    }
    // Below the config-driven card_logo_url: a hardcoded per-tenant default
    // for tenants that don't have that config set yet (defaultCardLogos.js).
    const src = cover || logoSrc || getDefaultCardLogo();
    if (src) {
      const img = document.createElement("img");
      img.className = "ws-card-img";
      img.src = src;
      if (!cover) img.classList.add("ws-rich-mem-logo");
      img.alt = club.title || "Membership";
      img.loading = "lazy";
      img.style.cursor = "pointer";
      img.addEventListener("click", openDetail);
      head.appendChild(img);
    }
    card.appendChild(head);

    // Title sits on the card (above the inner panel).
    if (club.title) {
      const title = document.createElement("p");
      title.className = "ws-card-title";
      title.title = club.title;
      title.textContent = club.title;
      card.appendChild(title);
    }

    const inner = document.createElement("div");
    inner.className = "ws-rich-inner";

    // Top line = price_additional_text (old .card-box-price), NOT price.
    if (club.price_additional_text) {
      const price = document.createElement("p");
      price.className = "ws-card-price";
      price.textContent = club.price_additional_text;
      inner.appendChild(price);
    }

    // Bullets = the BE `description` array (the marketing lines), NOT constructed
    // bottles/curated/shipping meta.
    const bullets = Array.isArray(club.description)
      ? club.description.filter(Boolean)
      : club.description ? [club.description] : [];
    if (bullets.length) {
      const list = document.createElement("ul");
      list.className = "ws-rich-bullets";
      bullets.forEach((text) => {
        const li = document.createElement("li");
        li.innerHTML = `<span class="ws-rich-check">${CHECK_ICON}</span><span>${esc(text)}</span>`;
        list.appendChild(li);
      });
      inner.appendChild(list);
    }

    // Learn more → detail follow-up (forwards unique_id).
    const learn = document.createElement("button");
    learn.type = "button";
    learn.className = "ws-card-learnmore";
    learn.innerHTML = `<span class="ws-learnmore-icon">${BOOK_ICON}</span><span>Learn more</span>`;
    learn.addEventListener("click", openDetail);
    inner.appendChild(learn);

    // Footer (row): price + price_unit (left), CTA (right). The price block is
    // ALWAYS rendered so a lone CTA stays bottom-right via space-between.
    const showButton = club.display_button === undefined ? true : isTrue(club.display_button);
    if (club.cta_link && showButton) {
      const footer = document.createElement("div");
      footer.className = "ws-rich-membership-footer";

      const pd = document.createElement("div");
      pd.className = "ws-rich-mem-price";
      const amount = document.createElement("p");
      amount.className = "ws-card-price-amount";
      amount.textContent = club.price || "";
      pd.appendChild(amount);
      const unit = document.createElement("p");
      unit.className = "ws-card-price-unit";
      unit.textContent = club.price_unit || "";
      pd.appendChild(unit);
      footer.appendChild(pd);

      // Commerce7 tenants with the wine-club membership feature turned on get
      // a real in-chat Join button (login-gated); everyone else keeps the
      // plain external link (old behavior — the logged-in in-widget join was
      // never actually wired up in the old codebase).
      if (isMembershipEnabled()) {
        const cta = document.createElement("button");
        cta.type = "button";
        cta.className = "ws-card-cta";
        cta.textContent = club.cta_text || "Join Now";
        cta.addEventListener("click", () => {
          if (isChatDisabled()) return;
          if (!getState().user?.customerToken) {
            emit("ws-account-open");
            return;
          }
          emit("ws-club-join", { title: club.title, club });
        });
        footer.appendChild(cta);
      } else {
        const cta = document.createElement("a");
        cta.className = "ws-card-cta";
        cta.href = club.cta_link;
        cta.target = "_blank";
        cta.rel = "noopener noreferrer";
        cta.textContent = club.cta_text || "Join Now";
        footer.appendChild(cta);
      }
      inner.appendChild(footer);
    }

    card.appendChild(inner);
    return card;
  }
}
